/**
 * Success cases: the asset's list, the case editor's writes, the anonymized preview and the share confirmation.
 * A case links to assets, so every write refreshes the cases and the assets (proof and revisions follow them).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../client';
import type { RevisionOut } from '../types';
import type { CaseCreateIn, CaseListOut, CaseOut, CaseUpdateIn } from '../types/cases';

export const caseKeys = {
  all: ['cases'] as const,
  list: (assetId: string | null) => ['cases', 'list', assetId] as const,
  anonymized: (caseId: string) => ['cases', 'anonymized', caseId] as const,
};

function useRefreshCases() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: caseKeys.all });
    // A case can back several assets: their proof, revisions and history all follow it.
    void client.invalidateQueries({ queryKey: ['assets', 'detail'] });
    void client.invalidateQueries({ queryKey: ['assets', 'history'] });
  };
}

/** The cases linked to an asset, or (no asset) every case the caller may see, for "Link existing case". */
export function useCases(assetId: string | null, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: caseKeys.list(assetId),
    queryFn: () => api<CaseListOut>(assetId ? `/api/cases?asset_id=${encodeURIComponent(assetId)}` : '/api/cases'),
    enabled,
  });
}

/** The anonymized version of a saved case: what clients would read once it is shared. */
export function useAnonymizedCase(caseId: string | null) {
  return useQuery({
    queryKey: caseKeys.anonymized(caseId ?? ''),
    queryFn: () => api<CaseOut>(`/api/cases/${caseId}/anonymized`),
    enabled: caseId !== null,
  });
}

export function useSaveCase() {
  const refresh = useRefreshCases();
  return useMutation({
    meta: { success: (saved: CaseOut) => `${saved.anonymized_label} saved` },
    mutationFn: ({ id, change }: { id?: string; change: CaseCreateIn | CaseUpdateIn }) =>
      id ? api<CaseOut>(`/api/cases/${id}`, 'PATCH', change) : api<CaseOut>('/api/cases', 'POST', change),
    onSuccess: refresh,
  });
}

export function useShareCase() {
  const refresh = useRefreshCases();
  return useMutation({
    meta: { success: (saved: CaseOut) => `${saved.anonymized_label} can now be shared with clients` },
    mutationFn: (caseId: string) => api<CaseOut>(`/api/cases/${caseId}/shareable`, 'POST', { confirm: true }),
    onSuccess: refresh,
  });
}

export function useMakeCaseInternal() {
  const refresh = useRefreshCases();
  return useMutation({
    meta: { success: (saved: CaseOut) => `${saved.anonymized_label} is internal again` },
    mutationFn: (caseId: string) => api<CaseOut>(`/api/cases/${caseId}/internal`, 'POST'),
    onSuccess: refresh,
  });
}

export function useLinkCase(assetId: string) {
  const refresh = useRefreshCases();
  return useMutation({
    meta: { success: 'Case linked' },
    mutationFn: ({ caseId, revision }: { caseId: string; revision: number }) =>
      api<RevisionOut>(`/api/assets/${assetId}/cases`, 'POST', { revision, case_id: caseId }),
    onSuccess: refresh,
  });
}

export function useUnlinkCase(assetId: string) {
  const refresh = useRefreshCases();
  return useMutation({
    meta: { success: 'Case unlinked' },
    mutationFn: ({ caseId, revision }: { caseId: string; revision: number }) =>
      api<RevisionOut>(`/api/assets/${assetId}/cases/${caseId}?revision=${revision}`, 'DELETE'),
    onSuccess: refresh,
  });
}
